import { Component, Input, OnInit, OnDestroy, inject } from '@angular/core';
import { Observable, Subject, of } from 'rxjs';
import { takeUntil, filter, switchMap, map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-plan-frequency',
  templateUrl: './plan-frequency.component.html',
  standalone: true,
})
export class PlanFrequencyComponent implements OnInit, OnDestroy {
  @Input() columnData!: Observable<any>;
  frequency = '';

  private readonly destroy$ = new Subject<void>();
  private readonly translate = inject(TranslateService);

  ngOnInit(): void {
    this.columnData
      .pipe(
        // 1. Unsubscribe automatically when component is destroyed
        takeUntil(this.destroy$),

        // 2. Filter out null/undefined data
        filter((cols) => !!cols),

        // 3. Switch to the translation stream (avoids nested subscriptions)
        switchMap((cols) => {
          const val = cols.billing_interval_value;
          const type = cols.billing_interval_type;

          // --- Case A: Handle Zero ---
          if (val === 0) {
            return of('');
          }

          // --- Case B: Define Exact Matches (Lookup Table) ---
          // These are "special" terms that don't need number formatting (e.g. "Weekly" instead of "1 x Week")
          const exactMatches: Record<string, Record<number, string>> = {
            Week: {
              1: 'common.PERIOD.WEEKLY',
              2: 'common.PERIOD.BIWEEKLY',
              3: 'common.PERIOD.3WEEKS',
              4: 'common.PERIOD.MONTHLY',
              13: 'common.PERIOD.QUARTERLY',
              26: 'common.PERIOD.HALFYEARLY',
              52: 'common.PERIOD.YEARLY',
            },
            Month: {
              1: 'common.PERIOD.MONTHLY',
              2: 'common.PERIOD.BIMONTHLY',
              3: 'common.PERIOD.QUARTERLY',
              6: 'common.PERIOD.HALFYEARLY',
              12: 'common.PERIOD.YEARLY',
            },
          };

          // Check if we have a special exact match
          if (exactMatches[type]?.[val]) {
            return this.translate.get(exactMatches[type][val]);
          }

          // --- Case C: Handle Generic Formatting (Fallbacks) ---
          // Logic for: "5 x Weeks", "7 Days", "1 Day", etc.

          let key = '';
          let formatter = (text: string) => `${val} x ${text}`; // Default format: "5 x Weeks"

          if (type === 'Week') {
            key = 'common.PERIOD.WEEKS';
          } else if (type === 'Month') {
            key = 'common.PERIOD.MONTH';
            formatter = (text: string) => `${val} ${text}`; // Month uses space (e.g. "5 Months")
          } else if (type === 'Day') {
            if (val === 1) {
              key = 'common.PERIOD.DAY';
              formatter = (text: string) => `1 ${text}`; // Special case: "1 Day"
            } else {
              key = 'common.PERIOD.DAYS';
            }
          }

          // Translate the fallback key and apply the format
          return key ? this.translate.get(key).pipe(map(formatter)) : of('');
        }),
      )
      .subscribe((finalString) => {
        this.frequency = finalString;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
